import React from 'react';
import { Send, Inbox, Filter, Gauge, ThumbsUp, AlertTriangle } from 'lucide-react';
import { pctDelta } from '../../utils/statisticsAggregation';

const NBSP = ' ';
const fmtPct = (v) => (v === null || v === undefined) ? '—' : `${v}${NBSP}%`;
const fmtNum = (v) => (v === null || v === undefined) ? '—' : String(v);
const fmtDurationDefault = (sec) => {
  if (sec === null || sec === undefined) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}${NBSP}min` : `${s}${NBSP}s`;
};

function Delta({ curr, prev }) {
  const d = pctDelta(curr, prev);
  if (d === null) return null;
  const up = d >= 0;
  return (
    <span className={`text-xs font-medium ${up ? 'text-emerald-600' : 'text-red-600'}`}>
      {up ? '↑' : '↓'} {Math.abs(d)}{NBSP}% vs. Vorperiode
    </span>
  );
}

function Tile({ icon: Icon, label, value, curr, prev, hint, title }) {
  return (
    <div title={title} className="bg-white dark:bg-slate-800 rounded-xl shadow-xs border border-slate-200/60 dark:border-slate-700/60 p-3">
      <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.375rem' }}>
        {Icon && <Icon size={13} style={{ flexShrink: 0, marginTop: '1px', opacity: 0.8 }} aria-hidden="true" />}
        <span>{label}</span>
      </div>
      <div className="text-xl font-bold text-slate-800 dark:text-slate-100 mt-1">{value}</div>
      <div className="mt-0.5"><Delta curr={curr} prev={prev} /></div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

export function KpiStrip({ current, previous, formatDuration = fmtDurationDefault }) {
  const evaluatedNote = `${current.evaluatedCount} von ${current.totalCount} ausgewertet`;
  // Inline grid (not Tailwind grid-cols-*): the prebuilt output.css lacks the
  // grid-cols-6 / md:/lg: responsive variants, so class-based grids collapse to
  // one column. auto-fit keeps the tiles compact and wraps responsively.
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: '0.6rem' }}>
      <Tile icon={Send} label="Versendete Fragebögen" value={fmtNum(current.sent)} curr={current.sent} prev={previous?.sent}
        hint="Generierte Fragebogen-Links" title="Anzahl generierter Fragebogen-Links." />
      <Tile icon={Inbox} label="Ergebnisse" value={fmtNum(current.submitted)} curr={current.submitted} prev={previous?.submitted}
        hint="ausgefüllte Fragebögen" title="Alle eingereichten Ergebnisse — dieselbe Zahl wie auf der Ergebnisse-Seite." />
      <Tile icon={Filter} label="Conversion" value={fmtPct(current.conversionPct)} curr={current.conversionPct} prev={previous?.conversionPct} />
      <Tile icon={Gauge} label="Ø-Score" value={fmtPct(current.avgScorePct)} curr={current.avgScorePct} prev={previous?.avgScorePct} hint={evaluatedNote} />
      <Tile icon={ThumbsUp} label="Empfohlen" value={fmtPct(current.recommendedPct)} curr={current.recommendedPct} prev={previous?.recommendedPct} hint={evaluatedNote} />
      <Tile icon={AlertTriangle} label="Unbearbeitet (Datenqualität)" value={fmtPct(current.untouchedPct)} curr={current.untouchedPct} prev={previous?.untouchedPct} />
    </div>
  );
}
